<template>
  <div class="note-page">
    <!-- 页面标题区 -->
    <header class="page-header">
      <div class="header-left">
        <div class="title-indicator"></div>
        <h2 class="page-title">我的笔记</h2>
      </div>
      <div class="header-right">
        <span class="note-count">共 {{ totalNotes }} 条笔记</span>
      </div>
    </header>

    <!-- 搜索和筛选区 -->
    <section class="filter-section">
      <div class="search-box">
        <i class="el-icon-search search-icon"></i>
        <input
          type="text"
          class="search-input"
          placeholder="搜索笔记内容"
          v-model="searchQuery"
        />
      </div>
      <select
        class="filter-select video-filter-select"
        v-model="selectedVideo"
        @scroll="handleVideoScroll"
        ref="videoSelect"
      >
        <option value="">按视频筛选</option>
        <option
          v-for="video in videoList"
          :key="video.videoId"
          :value="video.videoId"
        >
          {{ video.videoTitle }}
        </option>
        <option v-if="videoLoading" disabled>加载中...</option>
      </select>
      <select class="filter-select" v-model="sortOrder">
        <option value="desc">按时间排序（最新）</option>
        <option value="asc">按时间排序（最早）</option>
      </select>
    </section>

    <!-- 笔记列表 -->
    <section class="notes-section">
      <!-- Loading 遮罩 -->
      <div v-show="noteLoading" class="loading-overlay">
        <div class="loading-spinner">
          <div class="spinner"></div>
          <p class="loading-text">加载中...</p>
        </div>
      </div>

      <div v-show="!noteLoading" class="notes-content">
        <div v-for="(group, index) in noteGroups" :key="index" class="note-group">
        <!-- 视频标题 -->
        <div class="video-header">
          <i class="el-icon-video-play video-icon"></i>
          <h3 class="video-title">{{ group.videoTitle }}</h3>
          <span class="note-count-badge">{{ group.notes.length }} 条笔记</span>
        </div>

        <!-- 笔记卡片列表 -->
        <div class="notes-list">
          <article
            v-for="(note, noteIndex) in group.notes"
            :key="noteIndex"
            class="note-card"
          >
            <div class="note-content-wrapper">
              <!-- 左侧内容 -->
              <div class="note-main">
                <div class="note-header">
                  <span class="time-tag">{{ note.timestamp }}</span>
                  <span class="note-title">{{ note.title }}</span>
                </div>
                <div class="note-content">
                  <p>{{ note.content }}</p>
                </div>
              </div>

              <!-- 右侧视频缩略图 -->
              <div class="video-thumbnail">
                <img :src="note.thumbnail" alt="视频截图" />
                <div class="play-overlay">
                  <div class="play-button">
                    <i class="el-icon-video-play"></i>
                  </div>
                </div>
              </div>
            </div>

            <!-- 底部操作栏 -->
            <div class="note-footer">
              <span class="note-time">{{ note.createTime }}</span>
              <div class="note-actions">
                <button class="action-btn edit-btn" @click="handleEditNote(note)">
                  <i class="el-icon-edit"></i>
                  编辑
                </button>
                <button class="action-btn delete-btn" @click="handleDeleteNote(note.noteId)">
                  <i class="el-icon-delete"></i>
                  删除
                </button>
              </div>
            </div>
          </article>
        </div>
      </div>
      </div>
    </section>

    <!-- 编辑笔记弹窗 -->
    <div v-if="showEditDialog" class="dialog-overlay" @click.self="closeEditDialog">
      <div class="dialog-container">
        <div class="dialog-header">
          <h3 class="dialog-title">编辑笔记</h3>
          <button class="close-btn" @click="closeEditDialog">
            <i class="el-icon-close"></i>
          </button>
        </div>
        <div class="dialog-body">
          <div class="form-group">
            <label class="form-label">笔记内容</label>
            <textarea
              v-model="editingNote.content"
              class="form-textarea"
              placeholder="请输入笔记内容"
              rows="6"
            ></textarea>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="btn btn-cancel" @click="closeEditDialog">取消</button>
          <button class="btn btn-primary" @click="saveEditNote">保存</button>
        </div>
      </div>
    </div>

    <!-- 分页器 -->
    <div class="pagination">
      <div class="pagination-info">
        显示第 {{ (currentPage - 1) * pageSize + 1 }}-{{ Math.min(currentPage * pageSize, totalNotes) }} 条，共 {{ totalNotes }} 条记录
      </div>
      <div class="pagination-controls">
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="previousPage"
        >
          <i class="el-icon-arrow-left"></i>
        </button>

        <button
          v-for="page in visiblePages"
          :key="page"
          class="page-number"
          :class="{ active: page === currentPage }"
          @click="goToPage(page)"
        >
          {{ page }}
        </button>

        <span v-if="totalPages > 3" class="page-ellipsis">...</span>

        <button
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="nextPage"
        >
          <i class="el-icon-arrow-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getVideoManagePage, getNoteList, editUserStudyDetail, removeUserStudyDetail } from '@/api/api'

export default {
  name: 'Note',
  data() {
    return {
      // 搜索和筛选
      searchQuery: '',
      selectedVideo: '',
      sortOrder: 'desc',

      // 视频列表数据
      videoList: [],
      videoPageNumber: 1,
      videoPageSize: 10,
      videoTotal: 0,
      videoLoading: false,
      videoHasMore: true,

      // 笔记数据
      noteGroups: [],
      noteLoading: false,

      // 编辑弹窗相关
      showEditDialog: false,
      editingNote: {
        noteId: '',
        content: ''
      },

      // 分页相关
      currentPage: 1,
      pageSize: 10,
      totalNotes: 0
    }
  },
  computed: {
    // 总页数
    totalPages() {
      return Math.ceil(this.totalNotes / this.pageSize)
    },
    // 可见页码
    visiblePages() {
      const pages = []
      const maxVisible = 3
      for (let i = 1; i <= Math.min(maxVisible, this.totalPages); i++) {
        pages.push(i)
      }
      return pages
    }
  },
  mounted() {
    // 初始加载视频列表
    this.loadVideoList()
    // 初始加载笔记列表
    this.loadNoteList()
    // 添加下拉框滚动监听
    this.$nextTick(() => {
      const select = this.$refs.videoSelect
      if (select) {
        select.addEventListener('scroll', this.handleVideoScroll)
      }
    })
  },
  watch: {
    // 监听搜索关键词变化
    searchQuery() {
      this.currentPage = 1
      this.loadNoteList()
    },
    // 监听视频筛选变化
    selectedVideo() {
      this.currentPage = 1
      this.loadNoteList()
    },
    // 监听排序变化
    sortOrder() {
      this.currentPage = 1
      this.loadNoteList()
    },
    // 监听页码变化
    currentPage() {
      this.loadNoteList()
    }
  },
  beforeDestroy() {
    // 移除滚动监听
    const select = this.$refs.videoSelect
    if (select) {
      select.removeEventListener('scroll', this.handleVideoScroll)
    }
  },
  methods: {
    // 加载笔记列表
    async loadNoteList() {
      this.noteLoading = true
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          searchQuery: this.searchQuery,
          videoId: this.selectedVideo,
          sortOrder: this.sortOrder
        }
        const res = await getNoteList(params)

        if (res.code === 1000) {
          // 更新笔记分组数据
          this.noteGroups = res.data.noteGroups || []
          // 更新分页信息
          this.totalNotes = res.data.pagination.totalNotes || 0
          this.currentPage = res.data.pagination.currentPage || 1
        }
      } catch (error) {
        console.error('加载笔记列表失败:', error)
      } finally {
        this.noteLoading = false
      }
    },

    // 加载视频列表
    async loadVideoList(isLoadMore = false) {
      if (this.videoLoading || !this.videoHasMore) return

      this.videoLoading = true
      try {
        const params = {
          pageNumber: this.videoPageNumber,
          pageSize: this.videoPageSize
        }
        const res = await getVideoManagePage(params)

        if (res.success && res.code === 1000) {
          const records = res.data.records || []

          if (isLoadMore) {
            // 懒加载：追加数据
            this.videoList = [...this.videoList, ...records]
          } else {
            // 初始加载：替换数据
            this.videoList = records
          }

          this.videoTotal = res.data.total || 0

          // 判断是否还有更多数据
          if (this.videoList.length >= this.videoTotal) {
            this.videoHasMore = false
          } else {
            this.videoHasMore = true
          }
        }
      } catch (error) {
        console.error('加载视频列表失败:', error)
      } finally {
        this.videoLoading = false
      }
    },

    // 下拉框滚动事件处理
    handleVideoScroll(event) {
      const select = event.target
      // 计算是否滚动到底部（留20px的缓冲区）
      const isBottom = select.scrollHeight - select.scrollTop - select.clientHeight < 20

      if (isBottom && !this.videoLoading && this.videoHasMore) {
        // 加载下一页
        this.videoPageNumber++
        this.loadVideoList(true)
      }
    },

    // 上一页
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    // 下一页
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },
    // 跳转到指定页
    goToPage(page) {
      this.currentPage = page
    },

    // 打开编辑弹窗
    handleEditNote(note) {
      this.editingNote = {
        noteId: note.noteId,
        content: note.content
      }
      this.showEditDialog = true
    },

    // 关闭编辑弹窗
    closeEditDialog() {
      this.showEditDialog = false
      this.editingNote = {
        noteId: '',
        content: ''
      }
    },

    // 保存编辑
    async saveEditNote() {
      if (!this.editingNote.content || !this.editingNote.content.trim()) {
        this.$message.warning('笔记内容不能为空')
        return
      }

      try {
        const params = {
          id: this.editingNote.noteId,
          content: this.editingNote.content
        }
        const res = await editUserStudyDetail(params)

        if (res.code === 1000) {
          this.$message.success('编辑成功')
          this.closeEditDialog()
          // 重新加载笔记列表
          this.loadNoteList()
        } else {
          this.$message.error(res.message || res.msg || '编辑失败')
        }
      } catch (error) {
        console.error('编辑笔记失败:', error)
        this.$message.error('网络连接出错，请刷新重试')
      }
    },

    // 删除笔记
    async handleDeleteNote(noteId) {
      if (!noteId) {
        this.$message.error('笔记ID不存在')
        return
      }

      // 确认删除
      this.$confirm('确定要删除这条笔记吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await removeUserStudyDetail({ ids: noteId })

          if (res.code === 1000) {
            this.$message.success('删除成功')
            // 重新加载笔记列表
            this.loadNoteList()
          } else {
            this.$message.error(res.message || res.msg || '删除失败')
          }
        } catch (error) {
          console.error('删除笔记失败:', error)
          this.$message.error('系统内部错误,请联系管理员维护')
        }
      }).catch(() => {
        // 用户取消删除
      })
    }
  }
}
</script>

<style scoped>
.note-page {
  width: 100%;
}

/* 页面标题区 */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-indicator {
  width: 6px;
  height: 24px;
  background: #1a71ff;
  border-radius: 999px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.note-count {
  font-size: 14px;
  color: #64748b;
}

/* 搜索和筛选区 */
.filter-section {
  --search-control-width: 360px;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
}

.search-box {
  position: relative;
  flex: 0 0 var(--search-control-width);
  width: var(--search-control-width);
  min-width: 280px;
  max-width: var(--search-control-width);
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 20px;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 10px 12px 10px 40px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #0f172a;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #1a71ff;
  box-shadow: 0 0 0 3px rgba(26, 113, 255, 0.1);
}

.filter-select {
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 10px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.video-filter-select {
  flex: 0 0 var(--search-control-width);
  width: var(--search-control-width);
  min-width: var(--search-control-width);
  max-width: var(--search-control-width);
  padding-right: 36px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, #94a3b8 50%), linear-gradient(135deg, #94a3b8 50%, transparent 50%);
  background-position: calc(100% - 18px) calc(50% - 2px), calc(100% - 12px) calc(50% - 2px);
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
}

.video-filter-select option {
  max-width: var(--search-control-width);
}

.filter-select:focus {
  outline: none;
  border-color: #1a71ff;
  box-shadow: 0 0 0 3px rgba(26, 113, 255, 0.1);
}

/* 笔记列表 */
.notes-section {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 32px;
  min-height: 400px;
}

/* Loading 遮罩 */
.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(2px);
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #1a71ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  margin: 0;
}

.note-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 视频标题 */
.video-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
}

.video-icon {
  color: #1a71ff;
  font-size: 20px;
}

.video-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.note-count-badge {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 400;
  margin-left: 8px;
}

/* 笔记卡片列表 */
.notes-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.note-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: box-shadow 0.2s;
}

.note-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.note-content-wrapper {
  display: flex;
  gap: 20px;
  padding: 20px;
}

.note-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.note-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.time-tag {
  padding: 4px 8px;
  background: rgba(26, 113, 255, 0.05);
  color: #1a71ff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
}

.note-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.note-content {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
}

.note-content p {
  margin: 0;
}

/* 视频缩略图 */
.video-thumbnail {
  width: 192px;
  flex-shrink: 0;
  aspect-ratio: 16/9;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f5f9;
  position: relative;
  cursor: pointer;
}

.video-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}

.play-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(140, 150, 160, 0.8);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(4px);
  transition: background 0.2s;
}

.video-thumbnail:hover .play-button {
  background: rgba(140, 150, 160, 1);
}


/* 笔记底部操作栏 */
.note-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: rgba(248, 250, 252, 0.5);
  border-top: 1px solid #f1f5f9;
}

.note-time {
  font-size: 12px;
  color: #94a3b8;
}

.note-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}

.action-btn:hover {
  opacity: 0.8;
}


.edit-btn {
  color: #1a71ff;
}

.delete-btn {
  color: #f5222d;
}

/* 分页器 */
.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-top: 1px solid #e2e8f0;
}

.pagination-info {
  font-size: 14px;
  color: #64748b;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
}

.page-btn:not(:disabled):hover {
  background: #f8fafc;
}


.page-number {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.2s;
}

.page-number:hover {
  background: #f8fafc;
}

.page-number.active {
  background: #1a71ff;
  color: white;
  border-color: #1a71ff;
  box-shadow: 0 1px 3px rgba(26, 113, 255, 0.3);
}

.page-ellipsis {
  color: #cbd5e1;
  padding: 0 4px;
}


/* 编辑弹窗样式 */
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.dialog-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
}

.dialog-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f1f5f9;
  color: #0f172a;
}


.dialog-body {
  padding: 24px;
  overflow-y: auto;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.form-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #0f172a;
  line-height: 1.6;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;
}

.form-textarea:focus {
  outline: none;
  border-color: #1a71ff;
  box-shadow: 0 0 0 3px rgba(26, 113, 255, 0.1);
}

.form-textarea::placeholder {
  color: #94a3b8;
}

.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.btn {
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-cancel {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.btn-cancel:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.btn-primary {
  background: #1a71ff;
  color: white;
}

.btn-primary:hover {
  background: #1557d0;
}

/* 响应式 */
@media (max-width: 768px) {
  .filter-section {
    flex-direction: column;
  }

  .search-box {
    flex: 1 1 100%;
    width: 100%;
    max-width: 100%;
  }

  .filter-select {
    width: 100%;
  }

  .video-filter-select {
    flex: 1 1 100%;
    min-width: 0;
    max-width: 100%;
  }

  .note-content-wrapper {
    flex-direction: column;
  }

  .video-thumbnail {
    width: 100%;
  }

  .note-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .pagination {
    flex-direction: column;
    gap: 16px;
  }
}
</style>

